import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { captureAttribution } from "./attribution";
import { installOutboundTracking } from "./outbound";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function Seo({ title, description, image, path, jsonLd, type = "website", noindex = false }) {
  useEffect(() => {
    const fullTitle = title ? `${title}` : "Hotelstrips — Travel for the event. Stay for the experience.";
    document.title = fullTitle;
    const base = (window.__HS_BASE || window.location.origin).replace(/\/$/, "");
    const url = path ? `${base}${path}` : `${base}${window.location.pathname}`;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex,follow" : "index,follow");
    upsertLink("canonical", url);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:site_name", "Hotelstrips");
    upsertMeta("property", "og:image", image);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    let script = document.getElementById("hs-jsonld");
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = "hs-jsonld";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
  }, [title, description, image, path, type, jsonLd, noindex]);
  return null;
}

let _gaId = "";

// Loads Google's gtag.js ONLY after an explicit prior 'granted' choice — never on first visit, and
// never merely with a default-denied command (which would still fetch Google + send cookieless pings).
function _loadGtag() {
  // Production host only: a preview/staging Accept must never load or pollute the prod GA property.
  const host = new URL(window.location.origin).hostname;
  if (host !== "hotelstrips.com" && host !== "www.hotelstrips.com") return;
  if (!_gaId || window.__HS_GA || !_isPublicPath(window.location.pathname)) return;
  window.__HS_GA = true;
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${_gaId}`;
  document.head.appendChild(s);
  gtag("js", new Date());
  // Ads always denied (we run no ads); analytics granted because we only reach here on consent.
  gtag("consent", "default",
    { ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", analytics_storage: "granted" });
  // Manual events use query-less URLs. The Google stream must ALSO have automatic
  // enhanced-history pageviews disabled before production collection is enabled.
  gtag("config", _gaId, { send_page_view: false, anonymize_ip: true,
    page_location: `${window.location.origin}${window.location.pathname}`, page_referrer: _safeReferrer() });
}

// Loads public config once: sets canonical base + Search Console verification, and records the GA4
// Measurement ID — but does NOT load Google until the visitor has explicitly accepted analytics.
export function AnalyticsLoader() {
  useEffect(() => {
    captureAttribution();
    installOutboundTracking();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config`)
      .then((r) => r.json())
      .then((cfg) => {
        if (cfg.public_base_url) window.__HS_BASE = cfg.public_base_url;
        if (cfg.search_console_verification) {
          upsertMeta("name", "google-site-verification", cfg.search_console_verification);
        }
        _gaId = cfg.ga_measurement_id || process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
        window.__HS_GA_ID = _gaId;
        if (_gaId && hasAnalyticsConsent() === "granted") {
          _loadGtag();
          trackPageview(window.location.pathname);  // manual first pageview (auto is disabled)
        }
      })
      .catch(() => {});
  }, []);
  return null;
}

// 'granted' | 'denied' | null (undecided -> the banner is shown).
export function hasAnalyticsConsent() {
  try { return localStorage.getItem("hs_consent"); } catch (e) { return null; }
}

// Called by the cookie-consent UI. Accept lazy-loads GA (analytics only — ad storage stays denied,
// we run no ads); Decline/revoke keeps it off / sends a denied update. Persists across reloads. No PII.
export function setAnalyticsConsent(granted) {
  try { localStorage.setItem("hs_consent", granted ? "granted" : "denied"); } catch (e) { /* ignore */ }
  if (granted) {
    _loadGtag();
    if (typeof window.gtag === "function") window.gtag("consent", "update", { analytics_storage: "granted" });
    trackPageview(window.location.pathname);  // record the current page the moment consent is given
  } else if (typeof window.gtag === "function") {
    window.gtag("consent", "update", { analytics_storage: "denied" });
  }
}

// Admin/auth surfaces are never tracked. Everything else is a public path.
function _isPublicPath(pathname) {
  const p = (pathname || "/").toLowerCase();
  return !(p.startsWith("/admin") || p.startsWith("/login"));
}

function _safeReferrer() {
  try {
    const r = new URL(document.referrer);
    return `${r.origin}${r.pathname}`;
  } catch (e) { return ""; }
}

// Query-less manual page_view. Fires ONLY with explicit 'granted' consent, only on public paths,
// and sends page_location = origin+pathname (no query string) + a sanitized (query-less) referrer.
export function trackPageview(pathname) {
  if (typeof window.gtag !== "function") return;
  if (hasAnalyticsConsent() !== "granted") return;
  if (!_isPublicPath(pathname)) return;
  const origin = (window.location && window.location.origin) || "";
  window.gtag("event", "page_view", {
    page_location: `${origin}${pathname}`,
    page_path: pathname,
    page_referrer: _safeReferrer(),
  });
}

// Mounted once inside the Router: emits a query-less page_view on every client-side route change
// (public paths only; no-op without granted consent).
export function RouteAnalytics() {
  const location = useLocation();
  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);
  return null;
}

// Non-PII commerce signal: a booking CTA click carrying ONLY allowed event metadata (slugs, the stay
// date, and our own click reference / subid). NEVER a purchase/value event (a click is not a
// booking) and NEVER any form field or personal data. Fires only with granted consent — after a
// revoke it must not keep emitting cookieless events.
export function trackBookingClick({ productType, eventSlug, citySlug, eventDate, clientReference } = {}) {
  if (typeof window.gtag !== "function") return;
  if (hasAnalyticsConsent() !== "granted") return;
  if (!_isPublicPath(window.location.pathname)) return;
  window.gtag("event", "booking_click", {
    page_location: `${window.location.origin}${window.location.pathname}`,
    page_referrer: _safeReferrer(),
    product_type: productType || "hotels",
    event_slug: eventSlug || undefined,
    city_slug: citySlug || undefined,
    event_date: eventDate || undefined,
    client_reference: clientReference || undefined,
  });
}
